import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { eventsService } from '../../services/eventsService';
import { blacklistService } from '../../services/blacklistService';
import { format } from 'date-fns';

export const RecentActivity: React.FC = () => {
  const [tab, setTab] = React.useState(0);

  const { data: recentEvents, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', 'recent'],
    queryFn: () => eventsService.getAll({ page: 1, pageSize: 10 }),
    refetchInterval: 30000,
  });

  const { data: recentBlacklist, isLoading: blacklistLoading } = useQuery({
    queryKey: ['blacklist', 'recent'],
    queryFn: () =>
      blacklistService.getAll({ page: 1, pageSize: 10, sortBy: 'addedAt', sortDescending: true }),
    refetchInterval: 30000,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  return (
    <Box>
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Recent Events" />
        <Tab label="Recent Blacklist Additions" />
      </Tabs>

      {tab === 0 && (
        <TableContainer>
          {eventsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : !recentEvents || recentEvents.data.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography color="text.secondary">No recent events</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Source IP</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Protocol</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentEvents.data.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      {format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {event.sourceIp}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {event.sourceCountryCode ? (
                        <Chip
                          label={`${event.sourceCountryCode} ${event.sourceCity || ''}`}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {event.destinationIp}:{event.destinationPort}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={event.protocol?.toUpperCase() || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.action || 'N/A'}
                        size="small"
                        color={event.action === 'denied' ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer>
          {blacklistLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : !recentBlacklist || recentBlacklist.data.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography color="text.secondary">No recent blacklist additions</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Added</TableCell>
                  <TableCell>IP/Prefix</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Hit Count</TableCell>
                  <TableCell>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentBlacklist.data.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      {format(new Date(entry.addedAt), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {entry.ipAddress || entry.ipPrefix}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.reason}
                        size="small"
                        color={
                          entry.reason === 'threshold'
                            ? 'error'
                            : entry.reason === 'prefix_aggregation'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{entry.hitCount.toLocaleString()}</TableCell>
                    <TableCell>
                      {entry.lastActivity
                        ? format(new Date(entry.lastActivity), 'MMM dd, HH:mm')
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
};
